import { FormEvent, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, ExternalLink, Layers, LogOut, Monitor, Plus } from "lucide-react";
import { api, ApiError, WorkspaceSummary } from "@/lib/api";
import { slugify } from "@shared/slug";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScreensaverAppCard } from "@/components/admin/ScreensaverAppCard";

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  async function refresh() {
    try {
      const res = await api.listWorkspaces();
      setWorkspaces(res.workspaces);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load workspaces");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    try {
      await api.createWorkspace(name, slug || undefined);
      setOpen(false);
      setName("");
      setSlug("");
      await refresh();
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : "Failed to create workspace");
    } finally {
      setCreating(false);
    }
  }

  const effectiveSlug = slug || slugify(name);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-2 font-semibold">
            <Monitor className="h-5 w-5" /> Screensaver admin
          </div>
          <div className="flex items-center gap-4">
            <Button asChild variant="ghost" size="sm">
              <a href="/download" target="_blank" rel="noreferrer">
                <Download className="mr-1 h-4 w-4" /> Windows app
              </a>
            </Button>
            <Button asChild variant="ghost" size="sm">
              <Link to="/admin/shared-groups">
                <Layers className="mr-1 h-4 w-4" /> Shared groups
              </Link>
            </Button>
            <span className="text-sm text-muted-foreground">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={() => void logout()}>
              <LogOut className="mr-1 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="container py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Workspaces</h1>
            <p className="text-muted-foreground">Each workspace powers one kiosk screen.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-1 h-4 w-4" /> New workspace
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={create}>
                <DialogHeader>
                  <DialogTitle>Create workspace</DialogTitle>
                  <DialogDescription>
                    The slug becomes the kiosk URL: <code>/screensaver/&lt;slug&gt;</code>
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="ws-name">Name</Label>
                    <Input
                      id="ws-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Front Desk Lobby"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ws-slug">Slug (optional)</Label>
                    <Input
                      id="ws-slug"
                      value={slug}
                      onChange={(e) => setSlug(slugify(e.target.value))}
                      placeholder={slugify(name) || "front-desk-lobby"}
                    />
                    {effectiveSlug && (
                      <p className="text-xs text-muted-foreground">
                        Kiosk URL: /screensaver/{effectiveSlug}
                      </p>
                    )}
                  </div>
                  {createError && <p className="text-sm text-destructive">{createError}</p>}
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={creating}>
                    {creating ? "Creating…" : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-destructive">{error}</p>
        ) : workspaces.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No workspaces yet. Create your first one to get started.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {workspaces.map((ws) => (
              <Card key={ws.slug}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{ws.name}</CardTitle>
                    <Badge variant={ws.role === "owner" ? "default" : "secondary"}>{ws.role}</Badge>
                  </div>
                  <CardDescription>/screensaver/{ws.slug}</CardDescription>
                </CardHeader>
                <CardContent className="flex gap-2">
                  <Button asChild size="sm">
                    <Link to={`/admin/screensaver/${ws.slug}`}>Edit</Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <a href={`/screensaver/${ws.slug}`} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1 h-4 w-4" /> Open kiosk
                    </a>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-8">
          <ScreensaverAppCard />
        </div>
      </main>
    </div>
  );
}
